// Pure functions: GameState (or a fragment) in, view model out. No DOM, no
// store, no sim calls — which is what makes the screens testable and what lets
// PLAN-04 replace one function instead of the UI (PLAN-03 §4).
//
// Anything a screen needs that is not in GameState goes through here too, so
// the placeholder data has exactly one door into the shell.

import type {
  Breed,
  ChickenEntity,
  ChoreId,
  GameState,
  HousingType,
  Structure,
  WeatherCondition,
} from '../core/types';
import { t } from '../i18n';
import { chickenReferencePath, spritePath } from '../render/spriteManifest';
import { BREEDS } from '../sim/config';
import { formatForecastTempF, leagueForecast } from '../sim/forecast';
import {
  seasonChickPrice,
  seasonBagSizes,
  seasonVeteranWarningCooldownHours,
} from '../sim/season';
import {
  LAUNCH_BREEDS,
  breedEggBar,
  placeholderBinDaysLeft,
  placeholderNormalWarningCount,
  placeholderTameness,
  WARNING_WORST_ACCEPTABLE,
  placeholderVeteranWarningCount,
  placeholderYardSpots,
  type HousingCard,
  type LaunchBreedId,
  type SpotMarker,
} from './placeholders';
import { breedRole, breedTemperament, birdTemperament } from '../sim/breedProfile';
import {
  ALL_CHORES,
  applicableChores,
  averageLitterDirtiness,
  availableWater,
  brooderTsiFor,
  dueChores,
  LITTER_REBED_THRESHOLD,
  summarizeFacilities,
} from '../sim/welfare';
import { getConfig } from '../sim/config';
import type { BiologyConfig } from '../sim/config';
import { catchProbability } from '../sim/escape';
import { heatWatchActive } from '../sim/microclimate';
import { dayOf, formatClock } from '../sim/time';
import { evaluateChecklist } from '../sim/storm';
import { deriveMeters } from '../sim/meters';
import { isBroodyTendency } from '../sim/genetics';
import type { WeatherState } from '../core/types';
import { correctFeedFor, type MinigameId } from '../sim/minigames';
import {
  flockCap,
  flockCapFor,
  spotAvailability,
  spotDefs,
  unlockedSpots,
  type SpotKind,
  type SpotRefusal,
} from '../sim/spots';
import { statRevealDaysLeft, feederShortfall } from '../sim/access';
import { choreFact, factForTrigger, type FactCard } from '../edu/facts';

export interface BreedOption {
  breed: Breed;
  /** i18n key; the label itself comes from the existing breed strings. */
  nameKey: string;
  role: string;
  /**
   * The two party bars the game can source (REFOCUS §6): eggs off the real
   * per-week rate, temperament off the docility the sim runs escape and catch
   * odds on. Temperament is null for a breed no research covers, and the screen
   * leaves the row out rather than filling it in. There is no hardiness bar —
   * `sim/breedProfile.ts` says why, and a third bar is not coming back until
   * someone finds a hardiness scale to put on it.
   */
  bars: { eggs: number; temperament: number | null };
  price: number;
  /** No clay render yet for this breed — the row says so instead of faking it. */
  needsArt: boolean;
  sprite: string | null;
}

const BREED_NAME_KEY: Record<LaunchBreedId, string> = {
  silkie: 'geneticsAlbum.breed.silkie',
  leghorn: 'geneticsAlbum.breed.leghorn',
  rhode_island_red: 'geneticsAlbum.breed.rhode_island_red',
  australorp: 'geneticsAlbum.breed.australorp',
  isa_brown: 'geneticsAlbum.breed.isa_brown',
  brahma: 'geneticsAlbum.breed.brahma',
  plymouth_rock: 'geneticsAlbum.breed.plymouth_rock',
  buff_orpington: 'geneticsAlbum.breed.buff_orpington',
};

/**
 * True when biology-config.json actually carries numbers for this breed, which
 * also narrows a launch id to a breed the sim can create.
 */
export function hasBreedProfile(breed: LaunchBreedId): breed is Breed {
  return (BREEDS as unknown as Record<string, unknown>)[breed] !== undefined;
}

/**
 * The breeds the buy screen offers: REFOCUS §6's launch list, in mockup order,
 * filtered to the ones biology-config.json has numbers for.
 *
 * PLAN-01 C adds Buff Orpington. Until it is there the row is skipped rather
 * than shown with invented numbers (PLAN-03 §2) — never hardcode a fifth.
 */
export function selectBreedCatalog(): BreedOption[] {
  const out: BreedOption[] = [];
  for (const breed of LAUNCH_BREEDS) {
    if (!hasBreedProfile(breed)) continue;
    out.push({
      breed,
      nameKey: BREED_NAME_KEY[breed],
      role: breedRole(breed),
      bars: {
        eggs: breedEggBar(breed),
        temperament: breedTemperament(breed),
      },
      price: seasonChickPrice(breed),
      needsArt: false,
      sprite: chickenReferencePath(breed, false, 'hen'),
    });
  }
  return out;
}

export interface HousingView {
  housing: HousingType;
  nameKey: string;
  maxHens: number;
  chips: string[];
  sprite: string | null;
}

/**
 * The two cards on the first screen.
 *
 * The cap is the one `sim/spots.ts` enforces, read for Normal because Veteran
 * is not chosen until the chick screen — the card cannot promise a flock the
 * sim would then refuse. `placeholderFlockCap` is the retired duplicate of this
 * number and must not be used here.
 */
export function selectHousingCards(cards: HousingCard[]): HousingView[] {
  return cards.map((card) => ({
    housing: card.housing,
    nameKey: `home.${card.housing}`,
    maxHens: flockCapFor(card.housing, false),
    chips: card.chips,
    sprite: spritePath('struct', card.coopSprite),
  }));
}

/**
 * The Veteran mode flag, read from the one field the sim has today.
 *
 * CLEANUP C5 renames `state.veteran` to `state.veteran`; when that lands this
 * function is the only line that changes (PLAN-03 §4).
 */
export function selectVeteran(state: Readonly<GameState>): boolean {
  return state.veteran.enabled;
}

export function selectFlockCap(state: Readonly<GameState>): number {
  return flockCap(state);
}

/** Live birds, by pecking order then name, so the roster never shuffles. */
export function selectFlock(state: Readonly<GameState>): ChickenEntity[] {
  return state.flock
    .filter((bird) => bird.alive)
    .slice()
    .sort((a, b) => a.hierarchyRank - b.hierarchyRank || a.name.localeCompare(b.name));
}

export function selectBird(state: Readonly<GameState>, id: string): ChickenEntity | undefined {
  return state.flock.find((bird) => bird.id === id);
}

export function selectBreedName(breed: Breed): string {
  return t(BREED_NAME_KEY[breed] ?? 'geneticsAlbum.breed.silkie');
}

export function selectAgeWeeks(bird: ChickenEntity): number {
  return Math.floor(bird.ageInDays / 7);
}

// --- the coop screen (U3) -------------------------------------------------
//
export interface TopBarView {
  week: number;
  day: number;
  clock: string;
  eggits: number;
}

/** 7:42 am, from the sim's own clock. The old HUD formats it the same way. */
export function selectTopBar(state: Readonly<GameState>): TopBarView {
  // The sim's own clock, not a second copy of it: `dayOf` and `formatClock` are
  // what the HUD, the daylight gate and the chore windows already agree on.
  const day = dayOf(state.simHour);
  return {
    week: Math.floor(day / 7) + 1,
    day,
    clock: formatClock(state.simHour, state.settings.locale).replace('AM', 'am').replace('PM', 'pm'),
    eggits: Math.round(state.eggits),
  };
}


/** One league day as the pill's week strip draws it. */
export interface ForecastCell {
  leagueDay: number;
  /** Daytime high, formatted for the unit setting. */
  high: string;
  condition: WeatherCondition;
  storm: boolean;
}

export interface WeatherPillView {
  temp: string;
  humidity: string;
  location: string;
  /** i18n key for the alert tag, or null when there is nothing to say. */
  alertKey: string | null;
  alertTone: 'warn' | 'danger' | 'ink';
  /**
   * The league's coming week, or null in Normal — where there is no league and
   * the city's own weather stands alone (sim/forecast.ts).
   */
  outlook: ForecastCell[] | null;
}

export function selectWeatherPill(state: Readonly<GameState>): WeatherPillView {
  const weather: WeatherState = state.weather;
  const forecast = leagueForecast(state);
  const temp = state.settings.useCelsius
    ? `${Math.round(((weather.tempF - 32) * 5) / 9)}°C`
    : `${Math.round(weather.tempF)}°F`;
  const humid = weather.humidity >= 65;
  return {
    temp: humid ? `${temp} · ${t('weather.humid')}` : temp,
    humidity: humid ? '' : t('weather.dry'),
    // In a league the weather came from the season, not from a town, so the
    // pill names the league day instead of claiming a place the state does not
    // hold. Outside one, the city the player picked is still the honest answer.
    location: forecast
      ? t('weather.leagueDay', { day: forecast.today + 1 })
      : weather.locationLabel,
    // The heat tag is the sim's own severe-heat line applied to the open yard's
    // air (`sim/microclimate.ts`), so it is the league day's answer in a league
    // and the player's town's in Normal. A real storm alert always outranks it:
    // that one has been sim data for far longer.
    alertKey:
      weather.stormAlert !== 'none'
        ? `storm.alert.${weather.stormAlert}`
        : heatWatchActive(state)
          ? 'weather.heatWatch'
          : null,
    alertTone: weather.stormAlert === 'hit' || weather.stormAlert === 'warning' ? 'danger' : 'warn',
    outlook: forecast
      ? forecast.days.map((day) => ({
          leagueDay: day.leagueDay,
          high: formatForecastTempF(day.highF, state.settings.useCelsius),
          condition: day.condition,
          storm: day.stormAlert !== 'none',
        }))
      : null,
  };
}


export interface WarningBannerView {
  birdId: string;
  birdName: string;
  /** i18n key for the one-line cause. */
  causeKey: string;
  causeParams: Record<string, string | number>;
  /**
   * The right-hand label. It is the size of the ladder, not a count: no
   * per-bird warning history exists in GameState yet, and inventing one is
   * worse than saying what the ladder is (REFOCUS §3).
   */
  ladderLabel: string;
}

/**
 * The banner on the coop screen: the one bird in the flock whose worst vital is
 * at the distress line.
 *
 * This is the real path the placeholder stood in for. It reads the same three
 * vitals the warning screen shows, through the same helpers, so the banner and
 * the screen can never name two different problems for the same bird — and so
 * the banner can say heat, which the placeholder could not: it compared only
 * hydration and energy reserve, so a bird whose core was climbing through a
 * San Juan afternoon got no banner at all until she was also thirsty.
 *
 * No banner when the flock is fine. A warning that fires on a healthy hen is
 * the one thing this screen must never do (REFOCUS §11c).
 */
export function selectWarningBanner(state: Readonly<GameState>): WarningBannerView | null {
  const birds = state.flock.filter((bird) => bird.alive);
  if (birds.length === 0) return null;
  const config = getConfig(state.veteran.enabled ? 'veteran' : 'normal');

  let worst: { bird: ChickenEntity; vital: VitalKey; value: number } | null = null;
  for (const bird of birds) {
    for (const vital of worstVitals(bird, config)) {
      if (vital.value >= WARNING_WORST_ACCEPTABLE) continue;
      if (!worst || vital.value < worst.value) {
        worst = { bird, vital: vital.key, value: vital.value };
      }
    }
  }
  if (!worst) return null;

  const veteran = state.veteran.enabled;
  return {
    birdId: worst.bird.id,
    birdName: worst.bird.name,
    causeKey: `warning.cause.${worst.vital}`,
    causeParams: {
      name: worst.bird.name,
      temp: Math.round(state.weather.tempF),
      value: worst.value,
    },
    ladderLabel: t(veteran ? 'warning.ladderVeteranSize' : 'warning.ladderNormalSize', {
      count: veteran ? placeholderVeteranWarningCount : placeholderNormalWarningCount,
    }),
  };
}

export interface EscapeeView {
  id: string;
  name: string;
  breed: string;
  weeks: number;
  sprite: string;
  /** i18n key for the line that says how she will be to catch. */
  oddsKey: string;
  /** The sim's own number, which is what `oddsKey` is a word for. */
  catchChance: number;
}

/**
 * A bird that is out of the run, and how she is to get back (REFOCUS §12).
 *
 * The odds are the escape sim's number, not a second guess at it. The screen
 * never prints that number: Normal reads a word and Veteran reads the same
 * word, because a percentage is not what a keeper is holding.
 */
export function selectEscapees(state: Readonly<GameState>): EscapeeView[] {
  const facilities = summarizeFacilities(state);
  const config = getConfig(state.veteran.enabled ? 'veteran' : 'normal');
  const perimeter = {
    fenceHeightInches: facilities.fenceHeightInches,
    gateUnlatched: facilities.anyDoorOpen,
    cooped: false,
  };
  return selectFlock(state)
    .filter((bird) => bird.currentState === 'ESCAPED')
    .map((bird) => {
      const catchChance = catchProbability(bird, perimeter, config);
      return {
        id: bird.id,
        name: bird.name,
        breed: selectBreedName(bird.breed),
        weeks: selectAgeWeeks(bird),
        sprite: portraitSprite(bird),
        oddsKey: catchOddsKey(catchChance),
        catchChance,
      };
    });
}

export function selectEscapee(state: Readonly<GameState>, chickenId: string): EscapeeView | null {
  return selectEscapees(state).find((bird) => bird.id === chickenId) ?? null;
}

function catchOddsKey(chance: number): string {
  if (chance >= 0.4) return 'escapee.oddsGood';
  if (chance >= 0.2) return 'escapee.oddsEven';
  return 'escapee.oddsPoor';
}

export function selectSpotMarkers(state: Readonly<GameState>): SpotMarker[] {
  return placeholderYardSpots(state.housing);
}

export interface SpotRow {
  id: string;
  /** i18n key for the spot's name, out of the spot table. */
  nameKey: string;
  kind: SpotKind;
  /** The tile the renderer anchors anything placed here to. */
  world: { x: number; y: number };
  /** 'open', or the refusal a drop gets. */
  state: 'open' | SpotRefusal;
  /** Live birds, for the locked row's hint. */
  birds: number;
  /** The flock this spot opens at; 0 means always open. */
  unlockAtHens: number;
  /** The item standing here, by catalogue id, when something is. */
  occupantItemId?: string;
  /** The kind a dropped item wants, when this is the wrong one. */
  wantsKind?: SpotKind;
}

/**
 * Every spot this home has, open or not, with why.
 *
 * The marker overlay is still PLAN-04's mockup geometry, but the count it is
 * read for is the real table's: the coop screen says "yard spots · {count} of
 * {total}" and was passing the same number twice, so a backyard told a player it
 * would always have five when REFOCUS §7 says it unlocks to eight. `open` here
 * is the count that belongs in `{count}`.
 */
export function selectSpotRows(
  state: Readonly<GameState>,
  structureId?: string,
): SpotRow[] {
  const structure = structureId
    ? state.structures.find((s) => s.id === structureId)
    : undefined;
  return spotDefs(state.housing).flatMap((def) => {
    const available = spotAvailability(state, def.id, structure);
    if (!available) return [];
    const row: SpotRow = {
      id: def.id,
      nameKey: available.nameKey,
      kind: def.kind,
      world: def.world,
      state: available.state,
      birds: available.birds,
      unlockAtHens: available.unlockAtHens,
    };
    if (available.occupantItemId) row.occupantItemId = available.occupantItemId;
    if (available.wantsKind) row.wantsKind = available.wantsKind;
    return [row];
  });
}

/** How many yard spots are open against how many this home will ever have. */
export function selectYardSpotCount(state: Readonly<GameState>): { open: number; total: number } {
  const yard = spotDefs(state.housing).filter((spot) => spot.kind === 'yard');
  const open = unlockedSpots(state).filter((spot) => spot.kind === 'yard');
  return { open: open.length, total: yard.length };
}

export type BirdChipStatus =
  | 'escaped'
  | 'broody'
  | 'thirsty'
  | 'panting'
  | 'hungry'
  | 'stressed'
  | 'fine';

export interface BirdChipView {
  id: string;
  name: string;
  /** i18n key for the status line. */
  statusKey: string;
  tone: 'good' | 'warn' | 'plain';
  sprite: string;
}

/**
 * The one word a parent reads at a glance, in the order a keeper would notice
 * it: a broody hen is a decision, thirst and heat are emergencies (REFOCUS §11c).
 */
export function selectBirdChips(state: Readonly<GameState>): BirdChipView[] {
  return selectFlock(state).map((bird) => {
    const status = birdStatus(bird, state);
    return {
      id: bird.id,
      name: bird.name,
      statusKey: `bird.status.${status}`,
      tone: status === 'broody' ? 'good' : status === 'fine' ? 'plain' : 'warn',
      sprite: portraitSprite(bird),
    };
  });
}

export function birdStatus(bird: ChickenEntity, state: Readonly<GameState>): BirdChipStatus {
  // Being out of the run outranks every word about her body: a keeper scanning
  // the row needs to see that she is gone before why she looks thirsty.
  if (bird.currentState === 'ESCAPED') return 'escaped';
  if (bird.currentState === 'BROODY') return 'broody';
  const meters = deriveMeters(bird);
  if (bird.conditions.some((condition) => condition.id === 'heat_stress')) return 'panting';
  if (meters.thirst < 35) return 'thirsty';
  if (meters.hunger < 30) return 'hungry';
  if (meters.happiness < 35) return 'stressed';
  // A cold, wet Silkie is the breed's own distress line (REFOCUS §11c); the
  // temperature is the sim's, so the word is real.
  if (state.weather.tempF < 70 && bird.breed === 'silkie') return 'panting';
  return 'fine';
}


export type ChoreStatus = 'done' | 'due' | 'soon' | 'idle';

export interface ChoreRowView {
  id: ChoreId;
  label: string;
  status: ChoreStatus;
  /** i18n key for the line under the name, when the status needs a word. */
  detailKey: string;
  /** Pre-formatted value (the lock time) or null when the word is enough. */
  detail: string | null;
  tone: 'good' | 'warn' | 'plain' | 'idle';
  /** The meter the chore sheet explains, 0–100. */
  meter: { label: string; value: number; valueKey: string } | null;
  /** Eggits the quick action costs, where there is a cost. */
  cost: number | null;
}

export const CHORE_ICONS: Record<ChoreId, string> = {
  water: '<path d="M12 3c-3 4-6 7.5-6 11a6 6 0 0 0 12 0c0-3.5-3-7-6-11z"></path>',
  feed: '<path d="M4 11h16a8 8 0 0 1-16 0z"></path><path d="M8 8c1-2 2.5-3 4-3s3 1 4 3"></path>',
  litter: '<path d="M15 3 11 12"></path><path d="M6 12h10l2 9H4z"></path><path d="M9 16v5"></path><path d="M13 16v5"></path>',
  eggs: '<path d="M12 3c-3.5 0-6 5-6 9.5a6 6 0 0 0 12 0C18 8 15.5 3 12 3z"></path>',
  lock_coop: '<rect x="5" y="11" width="14" height="10" rx="2"></rect><path d="M8 11V7a4 4 0 0 1 8 0v4"></path>',
  storm_anchor:
    '<path d="M12 3v18"></path><path d="M4 9h16"></path><path d="M7 21h10"></path><path d="M4 9 2 15h4z"></path><path d="M20 9l-2 6h4z"></path>',
};

/**
 * One row per chore, straight off the sim's own answers. The old chore bar
 * decides done/due/idle from exactly these three sets, so the two UIs cannot
 * disagree (PLAN-03 U3 acceptance).
 */
export function selectChoreRows(state: Readonly<GameState>): ChoreRowView[] {
  const config = getConfig(state.veteran.enabled ? 'veteran' : 'normal');
  const due = new Set(dueChores(state, config));
  const done = new Set(state.progression.choresDoneToday);
  const canDo = new Set(applicableChores(state));

  return ALL_CHORES.map((id) => {
    const isDone = done.has(id);
    const isDue = due.has(id);
    // A yard with no coop cannot collect eggs or lock up. The old bar greys
    // those out rather than answering a tap with "no supplies".
    const isIdle = !canDo.has(id) && !isDone;
    const status: ChoreStatus = isDone ? 'done' : isIdle ? 'idle' : isDue ? 'due' : 'soon';
    return {
      id,
      label: t(`chore.${id}`),
      status,
      detailKey: `choreBar.state.${status}`,
      detail:
        id === 'lock_coop' && status === 'soon'
          ? formatClock(24 + state.weather.sunsetHour, state.settings.locale)
              .replace('AM', 'am')
              .replace('PM', 'pm')
          : null,
      tone: status === 'done' ? 'good' : status === 'due' ? 'warn' : status === 'idle' ? 'idle' : 'plain',
      meter: choreMeter(id, state),
      cost: id === 'litter' ? 2 : null,
    };
  });
}

function choreMeter(
  id: ChoreId,
  state: Readonly<GameState>,
): { label: string; value: number; valueKey: string } | null {
  switch (id) {
    case 'water': {
      const waterers = state.structures.filter((s) => s.state.water !== undefined);
      if (waterers.length === 0) return null;
      return {
        label: t('choreBar.meter.water'),
        value: Math.round(Math.max(...waterers.map((s) => s.state.water ?? 0))),
        valueKey: 'choreSheet.meterFull',
      };
    }
    case 'feed': {
      const feeders = state.structures.filter((s) => s.state.feed !== undefined);
      if (feeders.length === 0) return null;
      return {
        label: t('choreBar.meter.feed'),
        value: Math.round(Math.max(...feeders.map((s) => s.state.feed ?? 0))),
        valueKey: 'choreSheet.meterFull',
      };
    }
    case 'litter':
      return {
        label: t('choreBar.meter.litter'),
        value: Math.round(averageLitterDirtiness(state)),
        // Litter reads the other way up: more soiled is worse, and "0% full"
        // would be a lie about it.
        valueKey: 'choreSheet.meterSoiled',
      };
    case 'eggs': {
      const eggs = state.structures.reduce((sum, s) => sum + (s.state.eggs?.length ?? 0), 0);
      return {
        label: t('choreBar.meter.eggs'),
        value: eggs > 0 ? 100 : 0,
        valueKey: eggs > 0 ? 'choreSheet.meterWaiting' : 'choreSheet.meterFull',
      };
    }
    case 'lock_coop':
      return {
        label: t('choreBar.meter.door'),
        value: state.structures.some((s) => s.state.doorOpen !== false) ? 100 : 0,
        valueKey: state.structures.some((s) => s.state.doorOpen !== false)
          ? 'choreSheet.meterOpen'
          : 'choreSheet.meterShut',
      };
    case 'storm_anchor':
      // The whole prep list's score, which is the number the storm itself
      // scores. Showing anything else here — a count of coops, a fixed bar —
      // would be a number about anchoring on a screen whose subject is the
      // hurricane, and it would not move as the other five items get done.
      return {
        label: t('choreBar.meter.stormPrep'),
        value: evaluateChecklist(state).score,
        valueKey: 'choreSheet.meterPrep',
      };
  }
}

export { LITTER_REBED_THRESHOLD };

// --- the chore sheet (U4) --------------------------------------------------

export interface ChoreSheetView {
  /** i18n key for the title, e.g. `choreSheet.title.litter`. */
  titleKey: string;
  /** i18n key for the quick action, e.g. `choreSheet.quick.litter`. */
  quickKey: string;
  /** i18n key for the one-line reason under the meter. */
  reasonKey: string;
  reasonParams: Record<string, string | number>;
  meter: {
    label: string;
    value: number;
    valueKey: string;
    tone: 'good' | 'warn' | 'danger';
  } | null;
  /** The minigame this chore can open, or null when there is none. */
  minigame: MinigameId | null;
  /** i18n key for the minigame button, and its subtitle. */
  minigameKey: string | null;
  minigameHintKey: string | null;
  fact: FactCard | null;
}

/**
 * Which chores have a minigame (REFOCUS §12). `egg_wash` and `feed_mix` are the
 * two the sim has; the others are a one-tap chore, and the sheet says so by
 * having no Play button at all.
 */
const CHORE_MINIGAME: Partial<Record<ChoreId, MinigameId>> = {
  eggs: 'egg_wash',
  feed: 'feed_mix',
};

export function selectChoreSheet(state: Readonly<GameState>, chore: ChoreId): ChoreSheetView {
  const row = selectChoreRows(state).find((candidate) => candidate.id === chore);
  const minigame = CHORE_MINIGAME[chore] ?? null;
  return {
    titleKey: `choreSheet.title.${chore}`,
    quickKey: `choreSheet.quick.${chore}`,
    reasonKey: `choreSheet.reason.${chore}`,
    reasonParams: { temp: String(Math.round(state.weather.tempF)) },
    meter: row?.meter
      ? {
          label: row.meter.label,
          value: row.meter.value,
          valueKey: row.meter.valueKey,
          tone: meterTone(row.meter.value),
        }
      : null,
    minigame,
    minigameKey: minigame ? `choreSheet.play.${minigame}` : null,
    minigameHintKey: minigame ? `choreSheet.playHint.${minigame}` : null,
    fact: choreFact(chore) ?? null,
  };
}

function meterTone(value: number): 'good' | 'warn' | 'danger' {
  if (value >= 60) return 'danger';
  if (value >= 30) return 'warn';
  return 'good';
}

// --- the feed screen (U4) --------------------------------------------------

export type FeedType = 'starter' | 'grower' | 'layer';
export type BagSize = 'small' | 'big';

export interface FeedOption {
  type: FeedType;
  label: string;
  detail: string;
}

export interface FeedVerdict {
  correct: boolean;
  titleKey: string;
  bodyKey: string;
  bodyParams: Record<string, string | number>;
}

export interface BagOption {
  size: BagSize;
  label: string;
  days: number;
  cost: number;
  /** Cost per day, to two decimals, the way a keeper compares bags. */
  perDay: string;
  /** i18n key for the spoilage warning under the big bag, when it applies. */
  spoilKey: string | null;
}

/**
 * The feeder-space notice, and the card behind its "Why?".
 *
 * The two figures are the sim's, printed to one decimal: BHV-16's design
 * criterion is a number a keeper can check — all the birds at the trough at
 * once — so the honest thing to show is the division, not a verdict about
 * "too small". Rounding is presentation only, and happens here rather than in
 * the screen so the test can hold the shown string against the sim's figure.
 */
export interface FeederShortfallView {
  /** The sim's cm-per-bird, to one decimal. */
  space: string;
  /** The floor the sim compares it against, to one decimal. */
  need: string;
  birds: number;
  /** i18n key for the notice's one-line reason. */
  bodyKey: string;
  bodyParams: Record<string, string | number>;
  /** The card this unlock collects, the same one the Encyclopedia keeps. */
  fact: FactCard | null;
}

export function selectFeederShortfall(state: Readonly<GameState>): FeederShortfallView | null {
  const shortfall = feederShortfall(state, getConfig(state.veteran.enabled ? 'veteran' : 'normal'));
  if (!shortfall) return null;
  const space = shortfall.spacePerBird.toFixed(1);
  const need = shortfall.needPerBird.toFixed(1);
  return {
    space,
    need,
    birds: shortfall.birds,
    bodyKey: 'feed.shortfallBody',
    bodyParams: { birds: shortfall.birds, space, need },
    fact: factForTrigger('event:feeder_crowded') ?? null,
  };
}

export interface FeedView {
  flockLabel: string;
  week: number;
  /** How much is left in the open bin. */
  binDaysLeft: number;
  options: FeedOption[];
  correct: FeedType;
  verdict: FeedVerdict | null;
  bags: BagOption[];
  chosenBag: BagSize;
  cost: number;
  /** Oyster shell row. */
  calcium: { title: string; body: string; spot: string };
  /** BHV-16's shortfall notice, when the flock is short of trough. */
  shortfall: FeederShortfallView | null;
}

/** The three rations and the ages they are for (REFOCUS §12a). */
export const FEED_OPTIONS: FeedOption[] = [
  { type: 'starter', label: 'Starter', detail: '0–6 wk · high protein' },
  { type: 'grower', label: 'Grower', detail: '7–19 wk · low calcium' },
  { type: 'layer', label: 'Layer', detail: '20+ wk · high calcium' },
];

/**
 * The right ration is the youngest bird's: layer calcium damages growing
 * kidneys (NUT-04). `correctFeedFor` is the sim's own rule, shared with the
 * Feed Frenzy minigame.
 */
export function selectFeedView(state: Readonly<GameState>, chosen: FeedType | null, bag: BagSize): FeedView {
  const birds = selectFlock(state);
  const correct = correctFeedFor(state);
  const bags = selectBagOptions(state);
  const chosenBag = bags.find((option) => option.size === bag) ?? bags[0];
  return {
    flockLabel: t('feed.flock', { count: birds.length }),
    week: Math.floor(dayOf(state.simHour) / 7) + 1,
    binDaysLeft: placeholderBinDaysLeft(),
    options: FEED_OPTIONS,
    correct,
    verdict: chosen ? feedVerdict(birds, chosen, correct) : null,
    bags,
    chosenBag: chosenBag.size,
    cost: chosenBag.cost,
    calcium: {
      title: t('feed.calciumTitle'),
      body: t('feed.calciumBody'),
      // PLAN-04: the spot is real then. Until then the row says where it is
      // going rather than pretending it is placed.
      spot: t('feed.calciumSpot'),
    },
    shortfall: selectFeederShortfall(state),
  };
}

/** The verdict box: right or wrong, with the consequence spelled out (§12a). */
export function feedVerdict(
  birds: ChickenEntity[],
  chosen: FeedType,
  correct: FeedType,
): FeedVerdict {
  // The youngest bird decides the flock's ration, so the copy talks about her
  // age: "right for laying hens" would be nonsense for a brood of chicks.
  // An empty flock has no age; the verdict is never shown for one.
  const ages = birds.map((bird) => Math.floor(bird.ageInDays / 7));
  const youngest = ages.length > 0 ? Math.min(...ages) : 0;
  const oldest = ages.length > 0 ? Math.max(...ages) : 0;
  if (chosen === correct) {
    return {
      correct: true,
      titleKey: 'feed.verdictRightTitle',
      bodyKey: `feed.verdictRight${capitalise(correct)}Body`,
      bodyParams: { count: birds.length, weeks: youngest, oldest },
    };
  }
  // Wrong feed has a direction: too much calcium hurts the young, too little
  // leaves soft shells (REFOCUS §12a, NUT-04/NUT-05).
  const tooMuchCalcium = FEED_OPTIONS.findIndex((option) => option.type === chosen) >
    FEED_OPTIONS.findIndex((option) => option.type === correct);
  return {
    correct: false,
    titleKey: tooMuchCalcium ? 'feed.verdictTooMuchTitle' : 'feed.verdictTooLittleTitle',
    bodyKey: tooMuchCalcium ? 'feed.verdictTooMuchBody' : 'feed.verdictTooLittleBody',
    bodyParams: { weeks: youngest },
  };
}

function capitalise(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function selectBagOptions(state: Readonly<GameState>): BagOption[] {
  const humidHeat = state.weather.humidity >= 60 && state.weather.tempF >= 80;
  return seasonBagSizes().map((bag) => ({
    size: bag.size,
    label: t(`feed.bag.${bag.size}`),
    days: bag.days,
    cost: bag.cost,
    perDay: (bag.cost / bag.days).toFixed(2),
    // NUT-19: an open bag goes mouldy in 7–14 days of humid heat. Only the big
    // bag stays open that long, and only in that weather.
    spoilKey: bag.size === 'big' && humidHeat ? 'feed.spoilage' : null,
  }));
}

// --- the bird and warning screens (U5) -------------------------------------

export interface StageStripView {
  key: 'chick' | 'pullet' | 'adult';
  label: string;
  detail: string;
  active: boolean;
}

export interface StatBarView {
  key: 'eggs' | 'temperament';
  label: string;
  /** 0–100 of what the player can see so far. */
  learned: number;
  /** True while the rest of the stat is still hidden. */
  pending: boolean;
  /** "known", "4 more days", "learning". */
  stateKey: string;
  stateParams: Record<string, string | number>;
}

export interface BirdView {
  id: string;
  name: string;
  breed: string;
  /** Only once she is old enough for the sex to be known. */
  sexLine: string;
  weeks: number;
  role: string;
  sprite: string;
  statusKey: string;
  statusTone: 'good' | 'warn' | 'plain';
  stages: StageStripView[];
  chips: Array<{ key: string; text: string; tone: 'plain' | 'good' | 'warn' }>;
  stats: StatBarView[];
  /** I18n key for the Treat button, plus whether it can be pressed. */
  treatKey: string;
  treatEnabled: boolean;
}

/**
 * The portrait art for a bird. A pullet gets the hen's art: the only canonical
 * renders that exist are the chick and the adult, and a half-grown bird drawn
 * as a chick is worse than one drawn as a small hen.
 */
export function portraitSprite(bird: ChickenEntity): string {
  const isChick = bird.stage === 'chick' || bird.stage === 'egg';
  return chickenReferencePath(bird.breed, isChick, bird.gender);
}

/** Straight-run sex is a surprise until 6–8 weeks (REFOCUS §5). */
const SEX_REVEAL_DAYS = 42;

export function selectBirdView(state: Readonly<GameState>, bird: ChickenEntity): BirdView {
  const weeks = Math.floor(bird.ageInDays / 7);
  const known = bird.ageInDays >= SEX_REVEAL_DAYS;
  const status = birdStatus(bird, state);
  return {
    id: bird.id,
    name: bird.name,
    breed: selectBreedName(bird.breed),
    // Before the reveal the screen says what the player knows, not what the sim
    // secretly knows.
    sexLine: known ? t(`sex.${bird.gender}`) : t('sex.unknown'),
    weeks,
    role: breedRole(bird.breed),
    sprite: portraitSprite(bird),
    statusKey: `bird.status.${status}`,
    statusTone: status === 'broody' ? 'good' : status === 'fine' ? 'plain' : 'warn',
    stages: selectStageStrip(bird),
    chips: [
      { key: 'rank', text: t('bird.chipRank', { rank: bird.hierarchyRank + 1, of: state.flock.length }), tone: 'plain' },
      { key: 'personality', text: t(birdPersonalityKey(bird)), tone: 'plain' },
      { key: 'tameness', text: t('bird.chipTameness', { value: placeholderTameness(bird.ageInDays, hashOf(bird.id)) }), tone: 'plain' },
      { key: 'broody', text: t(broodyChipKey(bird)), tone: isBroodyTendency(bird.genotype) ? 'good' : 'plain' },
    ],
    stats: selectStatBars(state, bird),
    treatKey: bird.conditions.length > 0 ? 'bird.treat' : 'bird.treatNothing',
    treatEnabled: bird.conditions.length > 0,
  };
}

/** Chick → pullet → hen, each with the ration that stage needs (REFOCUS §12a). */
function selectStageStrip(bird: ChickenEntity): StageStripView[] {
  const order: Array<StageStripView['key']> = ['chick', 'pullet', 'adult'];
  const currentIndex = order.indexOf(bird.stage === 'egg' ? 'chick' : bird.stage);
  return order.map((key, index) => {
    const stage = key === 'adult' ? 'adult' : key;
    return {
      key,
      label: t(`flock.stage.${stage}`),
      detail: t(`bird.stageFeed.${key}`),
      active: index === currentIndex,
    };
  });
}

/**
 * The party bars the game can source (REFOCUS §6). Eggs is the one the player
 * has really seen — she has laid them. Temperament is the bird's own docility,
 * which is the number `sim/escape.ts` runs her escape and catch odds on, so a
 * learned value is a real reading and not a breed average wearing this bird's
 * name. It stays at 0 until the reveal day rather than creeping up to a
 * fraction of the answer: a bar that shows a third of a number nobody has
 * established yet is a number the screen invented. The countdown is the sim's
 * own (`sim/access.ts`), so it is 0 for a save written before the draw existed
 * rather than a wait that never ends.
 *
 * There is no hardiness bar, for the reason in `sim/breedProfile.ts`: the sim has
 * a per-bird hardiness multiplier and no scale to put on a bar. A bird whose
 * breed no research covers shows no temperament bar at all, which is the honest
 * shape for a game that does not know.
 */
function selectStatBars(state: Readonly<GameState>, bird: ChickenEntity): StatBarView[] {
  const day = dayOf(state.simHour);
  const eggs = Math.min(100, Math.round((bird.eggs.totalLaid / Math.max(1, bird.eggs.totalLaid + 4)) * 100));
  const temperament = birdTemperament(bird);
  const temperamentDays = statRevealDaysLeft(bird, 'temperament', day);
  const bars: StatBarView[] = [
    {
      key: 'eggs',
      label: t('bird.statEggs'),
      learned: bird.eggs.totalLaid > 0 ? eggs : 0,
      pending: false,
      stateKey: bird.eggs.totalLaid > 0 ? 'bird.statKnown' : 'bird.statWaiting',
      stateParams: {},
    },
  ];
  if (temperament !== null) {
    bars.push({
      key: 'temperament',
      label: t('bird.statTemperament'),
      learned: temperamentDays > 0 ? 0 : Math.min(100, Math.round(temperament)),
      pending: temperamentDays > 0,
      stateKey: temperamentDays > 0 ? 'bird.statInDays' : 'bird.statKnown',
      stateParams: { days: temperamentDays },
    });
  }
  return bars;
}

/** Bold or shy, from the breed's own flightiness in biology-config. */
export function birdPersonalityKey(bird: ChickenEntity): string {
  return (BREEDS[bird.breed]?.flightiness ?? 50) < 50 ? 'bird.bold' : 'bird.shy';
}

function broodyChipKey(bird: ChickenEntity): string {
  if (bird.currentState === 'BROODY') return 'bird.broodyNow';
  return isBroodyTendency(bird.genotype) ? 'bird.oftenBroody' : 'bird.rarelyBroody';
}

/** Small, stable, per-bird number for the placeholder stagger. */
function hashOf(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return hash;
}

// --- the warning screen (U5) -----------------------------------------------

export type VitalKey = 'water' | 'heat' | 'food';

export interface VitalView {
  key: VitalKey;
  label: string;
  value: number;
  /** Words for a value that is not a percentage (heat is not a percentage). */
  valueLabel: string;
  tone: 'good' | 'warn' | 'danger';
}

export interface WarningView {
  birdId: string;
  birdName: string;
  breed: string;
  weeks: number;
  sprite: string;
  vitals: VitalView[];
  /** False when every vital is fine, which the screen has to say out loud. */
  distressed: boolean;
  /** i18n key of the one-line cause. */
  causeKey: string;
  causeParams: Record<string, string | number>;
  /** i18n key of the primary fix, and the chore it runs. */
  fixKey: string;
  fixChore: ChoreId | null;
  ladder: {
    veteran: boolean;
    total: number;
    label: string;
    cooldownLabel: string | null;
  };
}

export function selectWarningView(state: Readonly<GameState>, bird: ChickenEntity): WarningView {
  const vitals = selectVitals(state, bird);
  // The worst vital is the cause and the fix. A keeper is not told "you have a
  // problem", she is told which bucket to fill. When nothing is wrong the
  // screen says so rather than inventing a cause.
  const worst = vitals.reduce((low, vital) => (vital.value < low.value ? vital : low));
  const distressed = worst.value < WARNING_WORST_ACCEPTABLE;
  const veteran = state.veteran.enabled;
  const total = veteran ? placeholderVeteranWarningCount : placeholderNormalWarningCount;
  return {
    birdId: bird.id,
    birdName: bird.name,
    breed: selectBreedName(bird.breed),
    weeks: Math.floor(bird.ageInDays / 7),
    sprite: portraitSprite(bird),
    vitals,
    distressed,
    causeKey: distressed ? `warning.cause.${worst.key}` : 'warning.nothingWrong',
    causeParams: {
      name: bird.name,
      temp: Math.round(state.weather.tempF),
      value: worst.value,
    },
    fixKey: distressed ? `warning.fix.${worst.key}` : 'warning.nothingToDo',
    fixChore: !distressed
      ? null
      : worst.key === 'water'
        ? 'water'
        : worst.key === 'food'
          ? 'feed'
          : null,
    ladder: {
      veteran,
      total,
      label: t(veteran ? 'warning.ladderVeteranTitle' : 'warning.ladderNormalTitle', { count: total }),
      // The cooldown only exists in Veteran, and the season owns the hours
      // (REFOCUS §11b).
      cooldownLabel: veteran
        ? t('warning.cooldown', { hours: seasonVeteranWarningCooldownHours() })
        : null,
    },
  };
}

export function selectVitals(state: Readonly<GameState>, bird: ChickenEntity): VitalView[] {
  const config = getConfig(state.veteran.enabled ? 'veteran' : 'normal');
  const vitals = worstVitals(bird, config);
  const raw = (key: VitalKey): number => vitals.find((entry) => entry.key === key)?.value ?? 100;
  const water = Math.round(raw('water'));
  const heat = raw('heat');
  const food = Math.round(raw('food'));
  return [
    {
      key: 'water',
      label: t('warning.vitalWater'),
      value: water,
      valueLabel: `${water}%`,
      tone: vitalTone(water),
    },
    {
      key: 'heat',
      label: t('warning.vitalHeat'),
      value: heat,
      valueLabel: heat >= 60 ? t('warning.heatHigh') : heat >= 30 ? t('warning.heatSome') : t('warning.heatLow'),
      tone: vitalTone(heat),
    },
    {
      key: 'food',
      label: t('warning.vitalFood'),
      value: food,
      valueLabel: `${food}%`,
      tone: vitalTone(food),
    },
  ];
}

/**
 * One bird's three vitals, as 0-100 where 100 is entirely well.
 *
 * Shared by the banner and the warning screen so the two cannot drift: the
 * banner picks the worst of them to decide whether to speak at all, and the
 * screen shows the same three.
 */
function worstVitals(
  bird: ChickenEntity,
  config: BiologyConfig,
): Array<{ key: VitalKey; value: number }> {
  // Heat stress is the bird's own core temperature, not the weather reading:
  // a two-week chick in a brooder at 84°F is fine and is meant to be warm
  // (BROODER_WARM_TOLERANCE_F, sim/welfare.ts), while a 105.5°F hen in Puerto
  // Rican afternoon heat is not. An ambient threshold would warn about every
  // chick on the island.
  const over = Math.max(0, bird.physiology.coreTemp - config.temperature.coreTempNormalF);
  const heatCondition = bird.conditions.some((condition) => condition.id === 'heat_stress');
  return [
    { key: 'water', value: bird.physiology.hydration },
    { key: 'heat', value: heatCondition ? 0 : clampPercent(100 - over * 25) },
    { key: 'food', value: bird.physiology.energyReserve },
  ];
}

function vitalTone(value: number): 'good' | 'warn' | 'danger' {
  if (value >= 60) return 'good';
  if (value >= 30) return 'warn';
  return 'danger';
}

// --- the first minute (REFOCUS §5) ------------------------------------------

export type LampVerdictKey =
  | 'onboard.brooder.verdictGood'
  | 'onboard.brooder.verdictCold'
  | 'onboard.brooder.verdictHot';

export interface LampOptionView {
  setting: number;
  labelKey: string;
  /** What the sim's own brooder rule says this dial does in today's weather. */
  verdictKey: LampVerdictKey;
}

/** The three dials a keeper actually turns, coldest first. */
export const LAMP_SETTINGS = [0, 50, 100] as const;

export interface BrooderView {
  options: LampOptionView[];
}

/**
 * What each lamp setting would do to the brood, read with the sim's own rule
 * (`brooderTsiFor`) against the weather the player actually has — so "too hot"
 * on a hot afternoon is the sim's verdict rather than a label written next to
 * the button. Empty only when there is no brooder to run, which is a save that
 * never went through onboarding.
 */
export function selectBrooderView(state: Readonly<GameState>): BrooderView {
  const config = getConfig(state.veteran.enabled ? 'veteran' : 'normal');
  const chick = selectFlock(state)[0];
  const brooder = summarizeFacilities(state).brooders[0];
  // No brood and no box is a save that never came through onboarding, and there
  // is nothing honest to say about a lamp that is not there.
  if (!chick || !brooder) return { options: [] };
  const readings = LAMP_SETTINGS.map((setting) =>
    brooderTsiFor(chick, withLamp(brooder, setting), state.weather.tempF, config),
  );
  const bestSetting = LAMP_SETTINGS[readings.indexOf(Math.min(...readings))];
  return {
    options: LAMP_SETTINGS.map((setting, index) => ({
      setting,
      labelKey: `onboard.brooder.lamp.${setting === 0 ? 'off' : setting === 50 ? 'low' : 'high'}`,
      verdictKey: lampVerdictKey(readings[index], setting, bestSetting, config.brooder.optimalTsiF),
    })),
  };
}

function withLamp(brooder: Structure, setting: number): Structure {
  return { ...brooder, state: { ...brooder.state, lampSetting: setting } };
}

/**
 * The stress index is degrees away from target and carries no sign, so which
 * side of the target a dial sits on comes from the lamp itself: it can only add
 * heat, and the index falls to zero at the setting that matches the chick's age.
 * So of the dials on screen, anything warmer than the best one is running hot.
 */
function lampVerdictKey(
  tsi: number,
  setting: number,
  bestSetting: number,
  optimalTsiF: number,
): LampVerdictKey {
  if (tsi <= optimalTsiF) return 'onboard.brooder.verdictGood';
  return setting > bestSetting ? 'onboard.brooder.verdictHot' : 'onboard.brooder.verdictCold';
}

export interface WaterView {
  /** 0–100 in the best drinker this yard has. */
  level: number;
  /** The sim's own answer to "can they drink this?", not a guess from the level. */
  drinkable: boolean;
}

/**
 * The first drink of the game (REFOCUS §5): the sim's own reading of the
 * waterers, so what the player is told is what the birds will actually get.
 */
export function selectWaterView(state: Readonly<GameState>): WaterView {
  const facilities = summarizeFacilities(state);
  const waterers = facilities.waterers;
  return {
    level:
      waterers.length === 0
        ? 0
        : Math.round(Math.max(...waterers.map((waterer) => waterer.state.water ?? 0))),
    drinkable: availableWater(facilities).has,
  };
}

function clampPercent(value: number): number {
  return Math.max(0, Math.min(100, value));
}
